import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid
} from 'recharts';

// Color palette for charts - enough distinct colors for many categories
const COLORS = [
  '#8b5cf6', '#6366f1', '#3b82f6', '#0ea5e9', '#06b6d4', '#14b8a6',
  '#10b981', '#22c55e', '#84cc16', '#eab308', '#f59e0b', '#f97316',
  '#ef4444', '#ec4899', '#d946ef', '#a855f7', '#7c3aed', '#4f46e5',
  '#2563eb', '#0284c7', '#0891b2', '#059669', '#65a30d', '#ca8a04',
  '#dc2626', '#db2777', '#c026d3', '#9333ea'
];

const OrganisationAnalyticsPage = () => {
  const [categoryDistribution, setCategoryDistribution] = useState(null);
  const [categoryTrends, setCategoryTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('12months');

  const periodOptions = [
    { value: '1month', label: 'Past 1 Month' },
    { value: '3months', label: 'Past 3 Months' },
    { value: '6months', label: 'Past 6 Months' },
    { value: '12months', label: 'Past 12 Months' },
    { value: '3years', label: 'Past 3 Years' },
    { value: '5years', label: 'Past 5 Years' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchTrends();
    }
  }, [selectedPeriod]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [distributionRes, trendsRes] = await Promise.all([
        axios.get(`${API}/super-admin/organisation-analytics/category-distribution`, { headers }),
        axios.get(`${API}/super-admin/organisation-analytics/category-trends?period=${selectedPeriod}`, { headers })
      ]);

      setCategoryDistribution(distributionRes.data);
      setCategoryTrends(trendsRes.data);
      
      // By default, show active categories (those with at least 1 org)
      if (trendsRes.data.active_categories) {
        setSelectedCategories(trendsRes.data.active_categories.slice(0, 8));
      }
    } catch (err) {
      console.error('Failed to fetch organisation analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    setTrendsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const trendsRes = await axios.get(
        `${API}/super-admin/organisation-analytics/category-trends?period=${selectedPeriod}`, 
        { headers }
      );

      setCategoryTrends(trendsRes.data);
      
      // Update selected categories if current selection is empty
      if (selectedCategories.length === 0 && trendsRes.data.active_categories) {
        setSelectedCategories(trendsRes.data.active_categories.slice(0, 8));
      }
    } catch (err) {
      console.error('Failed to fetch trends:', err);
    } finally {
      setTrendsLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const selectAllActiveCategories = () => {
    if (categoryTrends?.active_categories) {
      setSelectedCategories(categoryTrends.active_categories);
    }
  };

  const clearCategories = () => {
    setSelectedCategories([]);
  };

  // Custom tooltip for pie chart
  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = categoryDistribution?.total_organisations > 0
        ? ((data.count / categoryDistribution.total_organisations) * 100).toFixed(1)
        : 0;
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg">
          <p className="text-white font-medium">{data.category}</p>
          <p className="text-purple-400">{data.count} organisations</p>
          <p className="text-gray-400 text-sm">{percentage}% of total</p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for line chart
  const LineTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-lg max-w-xs">
          <p className="text-white font-medium mb-2">{label}</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-gray-300 text-sm truncate max-w-[150px]">{entry.name}</span>
                </div>
                <span className="text-white font-medium">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom legend for pie chart
  const renderPieLegend = (props) => {
    const { payload } = props;
    return (
      <div className="flex flex-wrap justify-center gap-2 mt-4">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-1.5 px-2 py-1 bg-slate-700/50 rounded">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-300 text-xs">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          <p className="mt-2 text-gray-600">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error Loading Analytics</h3>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button 
            onClick={fetchData}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Prepare line chart data
  const lineChartData = categoryTrends?.periods?.map((period, index) => {
    const dataPoint = { period };
    categoryTrends.trends.forEach((trend) => {
      if (selectedCategories.includes(trend.category)) {
        dataPoint[trend.category] = trend.data[index]?.count || 0;
      }
    });
    return dataPoint;
  }) || [];

  // Filter distribution for pie chart (only show categories with count > 0)
  const pieData = categoryDistribution?.distribution?.filter(d => d.count > 0) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white shadow-lg">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Organisation Analytics</h1>
        <p className="text-purple-200">Analyse organisation distribution and growth by business category</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Organisations</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {categoryDistribution?.total_organisations || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Active Categories</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {categoryTrends?.active_categories?.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Categories</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {categoryTrends?.trends?.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Pie Chart - Category Distribution */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Organisation Distribution by Category</h2>
          <p className="text-gray-500 text-sm mt-1">Breakdown of organisations across business categories</p>
        </div>
        <div className="p-6">
          {pieData.length > 0 ? (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="category"
                    label={({ category, percent }) => `${category.length > 15 ? category.substring(0, 15) + '...' : category} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={true}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend content={renderPieLegend} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
                <p>No organisations registered yet</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Full Category List */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">All Categories</h3>
            <button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className="text-sm text-purple-600 hover:text-purple-700"
            >
              {showAllCategories ? 'Hide' : 'Show All'}
            </button>
          </div>
          {showAllCategories && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {categoryDistribution?.distribution?.map((item, index) => (
                <div 
                  key={item.category}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm text-gray-700 truncate">{item.category}</span>
                  </div>
                  <span className={`text-sm font-medium ${item.count > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Line Chart - Category Trends Over Time */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Organisation Growth by Category</h2>
              <p className="text-gray-500 text-sm mt-1">Cumulative number of organisations over time</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Time Period:</label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                disabled={trendsLoading}
              >
                {periodOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {trendsLoading && (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
              )}
            </div>
          </div>
        </div>
        
        {/* Category Selection */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Select Categories to Display</span>
            <div className="flex gap-2">
              <button
                onClick={selectAllActiveCategories}
                className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
              >
                Select Active
              </button>
              <button
                onClick={clearCategories}
                className="text-xs px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {categoryTrends?.trends?.map((trend, index) => {
              const isActive = categoryTrends.active_categories?.includes(trend.category);
              const isSelected = selectedCategories.includes(trend.category);
              return (
                <button
                  key={trend.category}
                  onClick={() => toggleCategory(trend.category)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    isSelected
                      ? 'bg-purple-600 text-white'
                      : isActive
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: isSelected ? '#fff' : COLORS[index % COLORS.length] }}
                  />
                  {trend.category}
                  {!isActive && <span className="opacity-50">(0)</span>}
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="p-6">
          {selectedCategories.length > 0 ? (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineChartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="period" 
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    tickLine={{ stroke: '#d1d5db' }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<LineTooltip />} />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
                  />
                  {categoryTrends?.trends?.map((trend, index) => {
                    if (selectedCategories.includes(trend.category)) {
                      return (
                        <Line
                          key={trend.category}
                          type="monotone"
                          dataKey={trend.category}
                          name={trend.category}
                          stroke={COLORS[index % COLORS.length]}
                          strokeWidth={2}
                          dot={{ r: 4, fill: COLORS[index % COLORS.length] }}
                          activeDot={{ r: 6 }}
                        />
                      );
                    }
                    return null;
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                <p className="mb-2">Select categories to view trends</p>
                <button
                  onClick={selectAllActiveCategories}
                  className="text-sm px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Show Active Categories
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganisationAnalyticsPage;
